// src/components/floorplans/OutlineTemplateModal.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Icon } from "@iconify/react";
import api from "../../services/api";

/**
 * Web Mercator helpers (256px tile size)
 */
function lngToWorldX(lng, zoom) {
  const n = Math.pow(2, zoom);
  return ((lng + 180) / 360) * n * 256;
}

function latToWorldY(lat, zoom) {
  const n = Math.pow(2, zoom);
  const sinLat = Math.max(-0.9999, Math.min(0.9999, Math.sin((lat * Math.PI) / 180)));
  return (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * n * 256;
}

/**
 * Normalizes polygons so string or numeric lat/lng values are converted to finite numbers
 */
function normalizePolygons(polygons) {
  if (!Array.isArray(polygons)) return [];
  return polygons
    .map((poly) => {
      if (!Array.isArray(poly)) return [];
      return poly
        .map((p) => {
          if (!p) return null;
          const lat = Number(p.lat);
          const lng = Number(p.lng);
          return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
        })
        .filter(Boolean);
    })
    .filter((poly) => poly.length >= 3);
}

/**
 * Computes polygon geometric bounds, 5% calibrated upload bounds (matching floorplans.routes.js),
 * optimal tile zoom, and physical dimensions in meters.
 */
function getPolygonMetrics(polygons) {
  const validPolys = normalizePolygons(polygons);
  const allPoints = validPolys.flat();
  if (allPoints.length < 3) {
    return null;
  }

  // Tight vertex bounds (exact building footprint)
  const vertexNorth = Math.max(...allPoints.map((p) => p.lat));
  const vertexSouth = Math.min(...allPoints.map((p) => p.lat));
  const vertexEast = Math.max(...allPoints.map((p) => p.lng));
  const vertexWest = Math.min(...allPoints.map((p) => p.lng));

  // Exact 5% padding matching computeDefaultBounds() in server/routes/floorplans.routes.js
  // so 1:1 Calibration Fit templates align 100% automatically when uploaded as floorplans
  const rawLatSpan = Math.max(vertexNorth - vertexSouth, 0.0001);
  const rawLngSpan = Math.max(vertexEast - vertexWest, 0.0001);

  const north = vertexNorth + rawLatSpan * 0.05;
  const south = vertexSouth - rawLatSpan * 0.05;
  const east = vertexEast + rawLngSpan * 0.05;
  const west = vertexWest - rawLngSpan * 0.05;

  const centerLat = (vertexNorth + vertexSouth) / 2;
  const centerLng = (vertexEast + vertexWest) / 2;
  const rad = centerLat * (Math.PI / 180);

  // Building footprint physical dimensions in meters
  const buildingDeltaLat = Math.max(vertexNorth - vertexSouth, 0.000001);
  const buildingDeltaLng = Math.max(vertexEast - vertexWest, 0.000001);
  const buildingHeightMeters = Math.max(buildingDeltaLat * 110540, 1);
  const buildingWidthMeters = Math.max(buildingDeltaLng * 111320 * Math.cos(rad), 1);

  // Full padded bounding-box dimensions in meters
  const deltaLat = Math.max(north - south, 0.000001);
  const deltaLng = Math.max(east - west, 0.000001);
  const boxHeightMeters = Math.max(deltaLat * 110540, 1);
  const boxWidthMeters = Math.max(deltaLng * 111320 * Math.cos(rad), 1);
  const aspectRatio = Math.max(0.2, Math.min(5.0, boxWidthMeters / boxHeightMeters));

  // Approximate area in m^2 using planar shoelace formula across all polygons
  let areaM2 = 0;
  validPolys.forEach((poly) => {
    if (poly.length >= 3) {
      let sum = 0;
      for (let i = 0; i < poly.length; i++) {
        const j = (i + 1) % poly.length;
        const xi = (poly[i].lng - vertexWest) * 111320 * Math.cos(rad);
        const yi = (poly[i].lat - vertexSouth) * 110540;
        const xj = (poly[j].lng - vertexWest) * 111320 * Math.cos(rad);
        const yj = (poly[j].lat - vertexSouth) * 110540;
        sum += xi * yj - xj * yi;
      }
      areaM2 += Math.abs(sum) / 2;
    }
  });

  // Choose highest zoom level (up to 20) where the tile grid covering (north, south, east, west) is <= 36 tiles
  let tileZoom = 20;
  while (tileZoom > 14) {
    const minTX = Math.floor(lngToWorldX(west, tileZoom) / 256);
    const maxTX = Math.floor(lngToWorldX(east, tileZoom) / 256);
    const minTY = Math.floor(latToWorldY(north, tileZoom) / 256);
    const maxTY = Math.floor(latToWorldY(south, tileZoom) / 256);
    const count = (maxTX - minTX + 1) * (maxTY - minTY + 1);
    if (count <= 36) break;
    tileZoom--;
  }

  return {
    north,
    south,
    east,
    west,
    vertexNorth,
    vertexSouth,
    vertexEast,
    vertexWest,
    centerLat,
    centerLng,
    rad,
    widthMeters: buildingWidthMeters,
    heightMeters: buildingHeightMeters,
    boxWidthMeters,
    boxHeightMeters,
    aspectRatio,
    areaM2,
    optimalZoom: tileZoom,
    allPointsCount: allPoints.length,
  };
}

/**
 * Stitches XYZ map tiles from /api/public/map-tile into an exact bounding-box offscreen canvas
 */
async function stitchMapTilesForBounds(metrics, lyrs = "s", signal) {
  if (!metrics) return null;

  const zoom = metrics.optimalZoom;
  const minWorldX = lngToWorldX(metrics.west, zoom);
  const maxWorldX = lngToWorldX(metrics.east, zoom);
  const minWorldY = latToWorldY(metrics.north, zoom);
  const maxWorldY = latToWorldY(metrics.south, zoom);

  const minTileX = Math.floor(minWorldX / 256);
  const maxTileX = Math.floor(maxWorldX / 256);
  const minTileY = Math.floor(minWorldY / 256);
  const maxTileY = Math.floor(maxWorldY / 256);

  const cols = maxTileX - minTileX + 1;
  const rows = maxTileY - minTileY + 1;

  const gridCanvas = document.createElement("canvas");
  gridCanvas.width = cols * 256;
  gridCanvas.height = rows * 256;
  const gridCtx = gridCanvas.getContext("2d");

  const baseUrl = api.defaults.baseURL || "/api";

  const loadSingleTile = (tx, ty) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve({ tx, ty, img });
      img.onerror = () => resolve({ tx, ty, img: null });
      img.src = `${baseUrl}/public/map-tile?x=${tx}&y=${ty}&z=${zoom}&lyrs=${encodeURIComponent(lyrs)}`;
    });

  const promises = [];
  for (let tx = minTileX; tx <= maxTileX; tx++) {
    for (let ty = minTileY; ty <= maxTileY; ty++) {
      promises.push(loadSingleTile(tx, ty));
    }
  }

  const results = await Promise.all(promises);
  if (signal?.aborted) return null;

  let loadedCount = 0;
  results.forEach(({ tx, ty, img }) => {
    if (img) {
      loadedCount++;
      const dx = (tx - minTileX) * 256;
      const dy = (ty - minTileY) * 256;
      gridCtx.drawImage(img, dx, dy, 256, 256);
    }
  });

  if (loadedCount === 0) return null;

  // Crop the exact geographic bounding box from the stitched tile grid
  const cropX = Math.max(0, minWorldX - minTileX * 256);
  const cropY = Math.max(0, minWorldY - minTileY * 256);
  const cropW = Math.max(1, maxWorldX - minWorldX);
  const cropH = Math.max(1, maxWorldY - minWorldY);

  const croppedCanvas = document.createElement("canvas");
  // Maintain native high-res pixel density (at least 1600px on longest side)
  const scaleFactor = Math.max(1, 1600 / Math.max(cropW, cropH));
  croppedCanvas.width = Math.round(cropW * scaleFactor);
  croppedCanvas.height = Math.round(cropH * scaleFactor);
  const croppedCtx = croppedCanvas.getContext("2d");

  croppedCtx.drawImage(
    gridCanvas,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    croppedCanvas.width,
    croppedCanvas.height
  );

  return croppedCanvas;
}

export default function OutlineTemplateModal({
  show,
  onClose,
  allPolys = [],
  activePolyIndex = 0,
  locationName = "",
  storeCode = "",
  city = "",
}) {
  const [templateMode, setTemplateMode] = useState("georeference"); // 'georeference' | 'blueprint'
  const [selectedShape, setSelectedShape] = useState("all"); // 'all' | '0' | '1' ...
  const [bgStyle, setBgStyle] = useState("satellite"); // 'satellite' | 'hybrid' | 'map' | 'white' | 'transparent' | 'dark'
  const [previewTab, setPreviewTab] = useState("canvas"); // 'canvas' (WYSIWYG) | 'interactive' (Google Map)

  const [showGrid, setShowGrid] = useState(true);
  const [showVertices, setShowVertices] = useState(true);
  const [showEdgeLengths, setShowEdgeLengths] = useState(false);
  const [showDimensions, setShowDimensions] = useState(true);
  const [showNorthArrow, setShowNorthArrow] = useState(true);
  const [showTitleBlock, setShowTitleBlock] = useState(true);

  const [exportFormat, setExportFormat] = useState("png"); // 'png' | 'svg'
  const [exportResolution, setExportResolution] = useState(3000); // 2000 | 3000 | 4096

  const [stitchedMapCanvas, setStitchedMapCanvas] = useState(null);
  const [isLoadingTiles, setIsLoadingTiles] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const canvasRef = useRef(null);
  const mapContainerRef = useRef(null);
  const googleMapRef = useRef(null);
  const polygonInstanceRef = useRef(null);

  // Normalize all polygons once
  const normalizedAllPolys = useMemo(() => normalizePolygons(allPolys), [allPolys]);

  // Filter polygons based on selectedShape option
  const activePolygons = useMemo(() => {
    if (normalizedAllPolys.length === 0) return [];
    if (selectedShape === "all") return normalizedAllPolys;
    const idx = parseInt(selectedShape, 10);
    return normalizedAllPolys[idx] ? [normalizedAllPolys[idx]] : [normalizedAllPolys[0]];
  }, [normalizedAllPolys, selectedShape]);

  // Compute metrics
  const metrics = useMemo(() => {
    return getPolygonMetrics(activePolygons);
  }, [activePolygons]);

  const isMapOrSatellite = bgStyle === "satellite" || bgStyle === "hybrid" || bgStyle === "map";

  // Automatically stitch high-res tiles when modal opens or when bgStyle / metrics change
  useEffect(() => {
    if (!show || !metrics || !isMapOrSatellite) {
      return;
    }

    const controller = new AbortController();
    const lyrs = bgStyle === "satellite" ? "s" : bgStyle === "hybrid" ? "y" : "m";

    setIsLoadingTiles(true);
    stitchMapTilesForBounds(metrics, lyrs, controller.signal)
      .then((canvas) => {
        if (!controller.signal.aborted && canvas) {
          setStitchedMapCanvas(canvas);
        }
      })
      .catch((err) => {
        console.error("Error stitching map tiles:", err);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingTiles(false);
        }
      });

    return () => controller.abort();
  }, [show, metrics, isMapOrSatellite, bgStyle]);

  // Initialize and update optional interactive Google Map tab
  useEffect(() => {
    if (!show || !metrics || previewTab !== "interactive" || !mapContainerRef.current) return;
    if (!window.google || !window.google.maps) return;

    const mapTypeId = bgStyle === "map" ? "roadmap" : bgStyle === "hybrid" ? "hybrid" : "satellite";

    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: metrics.centerLat, lng: metrics.centerLng },
        zoom: metrics.optimalZoom,
        mapTypeId,
        tilt: 0,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "greedy",
      });
    } else {
      googleMapRef.current.setMapTypeId(mapTypeId);
      try {
        googleMapRef.current.setTilt(0);
      } catch (e) {}
    }

    const bounds = new window.google.maps.LatLngBounds(
      { lat: metrics.south, lng: metrics.west },
      { lat: metrics.north, lng: metrics.east }
    );
    googleMapRef.current.fitBounds(bounds, { top: 24, bottom: 24, left: 24, right: 24 });

    if (polygonInstanceRef.current) {
      polygonInstanceRef.current.setMap(null);
    }

    const isSat = bgStyle !== "map";
    const paths = activePolygons.map((poly) => poly.map((pt) => ({ lat: pt.lat, lng: pt.lng })));

    polygonInstanceRef.current = new window.google.maps.Polygon({
      paths,
      strokeColor: isSat ? "#fbbf24" : "#2563eb",
      strokeOpacity: 1.0,
      strokeWeight: 3,
      fillColor: isSat ? "#fbbf24" : "#3b82f6",
      fillOpacity: isSat ? 0.25 : 0.2,
      map: googleMapRef.current,
    });
  }, [show, previewTab, bgStyle, metrics, activePolygons]);

  // Clean up Google Map instance when modal closes
  useEffect(() => {
    if (!show) {
      googleMapRef.current = null;
      polygonInstanceRef.current = null;
    }
  }, [show]);

  // Draw template onto canvas (used for live WYSIWYG preview AND high-res PNG export)
  const renderCanvas = useCallback(
    (canvas, targetBaseSize = 3000, customMapImg = null, isPreview = false) => {
      if (!canvas || !metrics) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const scale = targetBaseSize / 3000;
      const isBlueprint = templateMode === "blueprint";
      const isGeoref = templateMode === "georeference";
      const effectiveMapImg = customMapImg || stitchedMapCanvas;
      const hasMap = isMapOrSatellite && effectiveMapImg;
      const isSat = bgStyle === "satellite" || bgStyle === "hybrid";
      const isDark = bgStyle === "dark" || isSat;

      let canvasW, canvasH;
      if (metrics.aspectRatio >= 1) {
        canvasW = Math.round(targetBaseSize);
        canvasH = Math.round(targetBaseSize / metrics.aspectRatio);
      } else {
        canvasH = Math.round(targetBaseSize);
        canvasW = Math.round(targetBaseSize * metrics.aspectRatio);
      }

      // Add border margin if in blueprint mode
      const marginRatio = isBlueprint ? 0.16 : 0.0;
      const totalW = Math.round(canvasW * (1 + marginRatio * 2));
      const totalH = Math.round(canvasH * (1 + marginRatio * 2));

      canvas.width = totalW;
      canvas.height = totalH;

      const offsetX = Math.round(canvasW * marginRatio);
      const offsetY = Math.round(canvasH * marginRatio);

      // Color scheme
      let strokeColor, fillColor, gridColor, textColor, subTextColor;
      if (isSat) {
        strokeColor = "#fbbf24"; // Radiant architectural amber/gold
        fillColor = "rgba(251, 191, 36, 0.20)";
        gridColor = "rgba(255, 255, 255, 0.22)";
        textColor = "#ffffff";
        subTextColor = "#fde68a";
      } else if (bgStyle === "map") {
        strokeColor = "#1d4ed8"; // Deep blueprint blue
        fillColor = "rgba(37, 99, 235, 0.16)";
        gridColor = "rgba(15, 23, 42, 0.12)";
        textColor = "#0f172a";
        subTextColor = "#334155";
      } else if (bgStyle === "dark") {
        strokeColor = "#38bdf8";
        fillColor = "rgba(56, 189, 248, 0.14)";
        gridColor = "rgba(255, 255, 255, 0.10)";
        textColor = "#f8fafc";
        subTextColor = "#94a3b8";
      } else {
        strokeColor = "#1e40af";
        fillColor = "rgba(37, 99, 235, 0.09)";
        gridColor = "rgba(15, 23, 42, 0.08)";
        textColor = "#0f172a";
        subTextColor = "#475569";
      }

      // 1. Background fill
      if (bgStyle === "transparent") {
        ctx.clearRect(0, 0, totalW, totalH);
        // Draw subtle checkerboard only on live preview so user sees transparency
        if (isPreview) {
          const checkerSize = Math.max(12, Math.round(24 * scale));
          for (let cy = 0; cy < totalH; cy += checkerSize) {
            for (let cx = 0; cx < totalW; cx += checkerSize) {
              const isEven = (Math.floor(cx / checkerSize) + Math.floor(cy / checkerSize)) % 2 === 0;
              ctx.fillStyle = isEven ? "#f8fafc" : "#e2e8f0";
              ctx.fillRect(cx, cy, checkerSize, checkerSize);
            }
          }
        }
      } else if (bgStyle === "dark") {
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, totalW, totalH);
      } else if (isSat) {
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, totalW, totalH);
      } else {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, totalW, totalH);
      }

      // 2. Render Stitched Map Image (already cropped to exact [west, east] x [north, south] bounds)
      if (hasMap) {
        ctx.save();
        try {
          ctx.drawImage(effectiveMapImg, offsetX, offsetY, canvasW, canvasH);
        } catch (e) {
          console.error("Canvas drawImage error:", e);
        }
        ctx.restore();
      }

      // Helper to project geographic (lat, lng) to canvas pixels using Web Mercator Y
      const minWY = latToWorldY(metrics.north, 20);
      const maxWY = latToWorldY(metrics.south, 20);
      const spanWY = Math.max(maxWY - minWY, 0.000001);

      const toPixel = (pt) => {
        const xNorm = (pt.lng - metrics.west) / (metrics.east - metrics.west);
        const ptWY = latToWorldY(pt.lat, 20);
        const yNorm = (ptWY - minWY) / spanWY;
        return {
          x: offsetX + xNorm * canvasW,
          y: offsetY + yNorm * canvasH,
        };
      };

      // 3. Metric Grid Lines (true 5m / 10m scale intervals)
      if (showGrid) {
        ctx.save();
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = Math.max(1.2 * scale, 1);

        // Pick a clean metric grid step (e.g. 5m, 10m, 20m, 50m)
        const maxDimM = Math.max(metrics.boxWidthMeters, metrics.boxHeightMeters);
        const metricStep = maxDimM <= 60 ? 5 : maxDimM <= 150 ? 10 : maxDimM <= 350 ? 20 : 50;
        const stepX = (metricStep / metrics.boxWidthMeters) * canvasW;
        const stepY = (metricStep / metrics.boxHeightMeters) * canvasH;

        for (let x = offsetX; x <= offsetX + canvasW + 0.5; x += stepX) {
          ctx.beginPath();
          ctx.moveTo(x, offsetY);
          ctx.lineTo(x, offsetY + canvasH);
          ctx.stroke();
        }
        for (let y = offsetY; y <= offsetY + canvasH + 0.5; y += stepY) {
          ctx.beginPath();
          ctx.moveTo(offsetX, y);
          ctx.lineTo(offsetX + canvasW, y);
          ctx.stroke();
        }
        ctx.restore();
      }

      // 4. Draw Building Outline Polygon(s)
      activePolygons.forEach((poly) => {
        if (!poly || poly.length < 3) return;

        ctx.save();
        ctx.beginPath();
        poly.forEach((pt, i) => {
          const { x, y } = toPixel(pt);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();

        // Fill interior
        ctx.fillStyle = fillColor;
        ctx.fill();

        // High-contrast outer casing for satellite/map legibility
        if (hasMap) {
          ctx.save();
          ctx.strokeStyle = isSat ? "rgba(0, 0, 0, 0.75)" : "rgba(255, 255, 255, 0.85)";
          ctx.lineWidth = Math.max(10 * scale, 5);
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.stroke();
          ctx.restore();
        }

        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = Math.max(6 * scale, 3);
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.stroke();

        // Draw edge lengths in meters if enabled
        if (showEdgeLengths) {
          ctx.font = `bold ${Math.max(11, Math.round(18 * scale))}px sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          for (let i = 0; i < poly.length; i++) {
            const p1 = poly[i];
            const p2 = poly[(i + 1) % poly.length];
            const dxM = (p2.lng - p1.lng) * 111320 * Math.cos(metrics.rad);
            const dyM = (p2.lat - p1.lat) * 110540;
            const edgeMeters = Math.sqrt(dxM * dxM + dyM * dyM);
            if (edgeMeters < 2.5) continue; // Skip tiny segments

            const px1 = toPixel(p1);
            const px2 = toPixel(p2);
            const midX = (px1.x + px2.x) / 2;
            const midY = (px1.y + px2.y) / 2;
            const label = `${edgeMeters.toFixed(1)}m`;

            const padX = 6 * scale;
            const padY = 4 * scale;
            const textW = ctx.measureText(label).width;

            ctx.fillStyle = isDark ? "rgba(15, 23, 42, 0.85)" : "rgba(255, 255, 255, 0.90)";
            ctx.fillRect(midX - textW / 2 - padX, midY - 10 * scale - padY, textW + padX * 2, 20 * scale + padY * 2);
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = Math.max(1.5 * scale, 1);
            ctx.strokeRect(midX - textW / 2 - padX, midY - 10 * scale - padY, textW + padX * 2, 20 * scale + padY * 2);

            ctx.fillStyle = isDark ? "#fde68a" : "#1e3a8a";
            ctx.fillText(label, midX, midY);
          }
        }

        // Draw vertex dots & numbers
        if (showVertices) {
          poly.forEach((pt, idx) => {
            const { x, y } = toPixel(pt);
            ctx.beginPath();
            ctx.arc(x, y, Math.max(8 * scale, 4), 0, Math.PI * 2);
            ctx.fillStyle = isSat ? "#fbbf24" : isDark ? "#38bdf8" : "#1e40af";
            ctx.fill();
            ctx.strokeStyle = isSat ? "#000000" : isDark ? "#0f172a" : "#ffffff";
            ctx.lineWidth = Math.max(3 * scale, 1.5);
            ctx.stroke();

            if ((isBlueprint || showVertices) && poly.length <= 40) {
              ctx.save();
              ctx.font = `bold ${Math.max(10, Math.round(18 * scale))}px sans-serif`;
              const label = String(idx + 1);
              const lx = x + 12 * scale;
              const ly = y - 10 * scale;
              ctx.lineWidth = Math.max(3 * scale, 2);
              ctx.strokeStyle = isDark ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.9)";
              ctx.strokeText(label, lx, ly);
              ctx.fillStyle = isDark ? "#ffffff" : "#0f172a";
              ctx.fillText(label, lx, ly);
              ctx.restore();
            }
          });
        }
        ctx.restore();
      });

      // 5. Georeference Bounding Box Border (1:1 calibration crop outline)
      if (isGeoref) {
        ctx.save();
        ctx.strokeStyle = isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(15, 23, 42, 0.30)";
        ctx.lineWidth = Math.max(3 * scale, 1.5);
        ctx.setLineDash([10 * scale, 8 * scale]);
        ctx.strokeRect(0, 0, totalW, totalH);

        // Corner calibration L-marks
        const markLen = 45 * scale;
        ctx.setLineDash([]);
        ctx.lineWidth = Math.max(6 * scale, 3);
        ctx.strokeStyle = strokeColor;

        // Top-left
        ctx.beginPath();
        ctx.moveTo(0, markLen);
        ctx.lineTo(0, 0);
        ctx.lineTo(markLen, 0);
        ctx.stroke();

        // Top-right
        ctx.beginPath();
        ctx.moveTo(totalW - markLen, 0);
        ctx.lineTo(totalW, 0);
        ctx.lineTo(totalW, markLen);
        ctx.stroke();

        // Bottom-left
        ctx.beginPath();
        ctx.moveTo(0, totalH - markLen);
        ctx.lineTo(0, totalH);
        ctx.lineTo(markLen, totalH);
        ctx.stroke();

        // Bottom-right
        ctx.beginPath();
        ctx.moveTo(totalW - markLen, totalH);
        ctx.lineTo(totalW, totalH);
        ctx.lineTo(totalW, totalH - markLen);
        ctx.stroke();

        ctx.restore();
      }

      // 6. Blueprint Annotations: Dimensions, North Arrow, Title Block
      if (isBlueprint) {
        ctx.save();

        // Inner map frame border
        ctx.strokeStyle = isDark ? "rgba(255,255,255,0.25)" : "#cbd5e1";
        ctx.lineWidth = Math.max(2 * scale, 1);
        ctx.strokeRect(offsetX, offsetY, canvasW, canvasH);

        // Dimension lines (showing exact building footprint width & height)
        if (showDimensions) {
          const topLeftPx = toPixel({ lat: metrics.vertexNorth, lng: metrics.vertexWest });
          const bottomRightPx = toPixel({ lat: metrics.vertexSouth, lng: metrics.vertexEast });

          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = Math.max(2.5 * scale, 1.5);
          ctx.fillStyle = textColor;
          ctx.font = `bold ${Math.max(11, Math.round(24 * scale))}px sans-serif`;
          ctx.textAlign = "center";

          // Top width dimension line
          const dimY = offsetY - 34 * scale;
          ctx.beginPath();
          ctx.moveTo(topLeftPx.x, dimY);
          ctx.lineTo(bottomRightPx.x, dimY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(topLeftPx.x, dimY - 12 * scale);
          ctx.lineTo(topLeftPx.x, dimY + 12 * scale);
          ctx.moveTo(bottomRightPx.x, dimY - 12 * scale);
          ctx.lineTo(bottomRightPx.x, dimY + 12 * scale);
          ctx.stroke();

          ctx.fillText(
            `↔ Building Width: ${metrics.widthMeters.toFixed(1)} m`,
            (topLeftPx.x + bottomRightPx.x) / 2,
            dimY - 16 * scale
          );

          // Left height dimension line
          const dimX = offsetX - 34 * scale;
          ctx.beginPath();
          ctx.moveTo(dimX, topLeftPx.y);
          ctx.lineTo(dimX, bottomRightPx.y);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(dimX - 12 * scale, topLeftPx.y);
          ctx.lineTo(dimX + 12 * scale, topLeftPx.y);
          ctx.moveTo(dimX - 12 * scale, bottomRightPx.y);
          ctx.lineTo(dimX + 12 * scale, bottomRightPx.y);
          ctx.stroke();

          ctx.save();
          ctx.translate(dimX - 18 * scale, (topLeftPx.y + bottomRightPx.y) / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(`↕ Building Length: ${metrics.heightMeters.toFixed(1)} m`, 0, 0);
          ctx.restore();
        }

        // North Arrow Indicator
        if (showNorthArrow) {
          const arrowX = totalW - 95 * scale;
          const arrowY = 95 * scale;
          const arrowSize = 46 * scale;

          ctx.save();
          ctx.translate(arrowX, arrowY);

          ctx.beginPath();
          ctx.arc(0, 0, arrowSize, 0, Math.PI * 2);
          ctx.fillStyle = isDark ? "rgba(15, 23, 42, 0.8)" : "rgba(255, 255, 255, 0.9)";
          ctx.fill();
          ctx.strokeStyle = isDark ? "rgba(255,255,255,0.4)" : subTextColor;
          ctx.lineWidth = Math.max(2 * scale, 1);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(0, -arrowSize + 6 * scale);
          ctx.lineTo(14 * scale, 12 * scale);
          ctx.lineTo(0, 4 * scale);
          ctx.closePath();
          ctx.fillStyle = strokeColor;
          ctx.fill();

          ctx.beginPath();
          ctx.moveTo(0, -arrowSize + 6 * scale);
          ctx.lineTo(-14 * scale, 12 * scale);
          ctx.lineTo(0, 4 * scale);
          ctx.closePath();
          ctx.fillStyle = isDark ? "#ffffff" : "#475569";
          ctx.fill();

          ctx.font = `bold ${Math.max(10, Math.round(20 * scale))}px sans-serif`;
          ctx.fillStyle = textColor;
          ctx.textAlign = "center";
          ctx.fillText("N", 0, -arrowSize - 10 * scale);

          ctx.restore();
        }

        // Title Block in Bottom Right
        if (showTitleBlock) {
          const tbW = Math.min(720 * scale, totalW * 0.48);
          const tbH = 156 * scale;
          const tbX = totalW - tbW - 24 * scale;
          const tbY = totalH - tbH - 24 * scale;

          ctx.fillStyle = isDark ? "rgba(15, 23, 42, 0.92)" : "rgba(248, 250, 252, 0.95)";
          ctx.strokeStyle = isDark ? "#334155" : "#cbd5e1";
          ctx.lineWidth = Math.max(2 * scale, 1);

          ctx.fillRect(tbX, tbY, tbW, tbH);
          ctx.strokeRect(tbX, tbY, tbW, tbH);

          ctx.textAlign = "left";
          ctx.font = `bold ${Math.max(11, Math.round(22 * scale))}px sans-serif`;
          ctx.fillStyle = isDark ? "#ffffff" : "#0f172a";
          ctx.fillText(locationName || "Building Outline Template", tbX + 18 * scale, tbY + 34 * scale);

          ctx.font = `${Math.max(9, Math.round(15 * scale))}px sans-serif`;
          ctx.fillStyle = isDark ? "#cbd5e1" : "#475569";
          const storeInfo = storeCode ? `Code: ${storeCode} • ` : "";
          const cityInfo = city ? `City: ${city}` : "";
          const styleLabel =
            bgStyle === "satellite"
              ? " • Satellite"
              : bgStyle === "hybrid"
              ? " • Hybrid"
              : bgStyle === "map"
              ? " • Roadmap"
              : "";
          ctx.fillText(`${storeInfo}${cityInfo}${styleLabel}`, tbX + 18 * scale, tbY + 60 * scale);

          ctx.fillText(
            `Footprint: ~${Math.round(metrics.areaM2).toLocaleString()} m² | Building: ${metrics.widthMeters.toFixed(1)}m × ${metrics.heightMeters.toFixed(1)}m`,
            tbX + 18 * scale,
            tbY + 86 * scale
          );

          ctx.font = `${Math.max(8, Math.round(13 * scale))}px monospace`;
          ctx.fillText(
            `Bounds: N ${metrics.north.toFixed(6)}, S ${metrics.south.toFixed(6)}, E ${metrics.east.toFixed(6)}, W ${metrics.west.toFixed(6)}`,
            tbX + 18 * scale,
            tbY + 114 * scale
          );

          ctx.fillText(
            `LoQation 360 • 1:1 Georeferenced CAD & Floorplan Template`,
            tbX + 18 * scale,
            tbY + 138 * scale
          );
        }

        ctx.restore();
      }
    },
    [
      metrics,
      templateMode,
      bgStyle,
      isMapOrSatellite,
      showGrid,
      showVertices,
      showEdgeLengths,
      showDimensions,
      showNorthArrow,
      showTitleBlock,
      activePolygons,
      locationName,
      storeCode,
      city,
      stitchedMapCanvas,
    ]
  );

  // Render live WYSIWYG preview canvas whenever any option or stitched tile canvas changes
  useEffect(() => {
    if (show && canvasRef.current && metrics) {
      renderCanvas(canvasRef.current, 1000, stitchedMapCanvas, true);
    }
  }, [show, renderCanvas, metrics, stitchedMapCanvas]);

  // Generate complete SVG string (including embedded satellite/map raster and Blueprint elements)
  const generateSvgString = useCallback(
    (mapCanvasForSvg = null) => {
      if (!metrics) return "";

      const isBlueprint = templateMode === "blueprint";
      const isGeoref = templateMode === "georeference";
      const isSat = bgStyle === "satellite" || bgStyle === "hybrid";
      const isDark = bgStyle === "dark" || isSat;
      const baseSize = 2000;

      let canvasW, canvasH;
      if (metrics.aspectRatio >= 1) {
        canvasW = baseSize;
        canvasH = Math.round(baseSize / metrics.aspectRatio);
      } else {
        canvasH = baseSize;
        canvasW = Math.round(baseSize * metrics.aspectRatio);
      }

      const marginRatio = isBlueprint ? 0.16 : 0.0;
      const totalW = Math.round(canvasW * (1 + marginRatio * 2));
      const totalH = Math.round(canvasH * (1 + marginRatio * 2));
      const offsetX = Math.round(canvasW * marginRatio);
      const offsetY = Math.round(canvasH * marginRatio);

      const strokeColor = isSat ? "#fbbf24" : isDark ? "#38bdf8" : "#1e40af";
      const fillColor = isSat
        ? "rgba(251, 191, 36, 0.20)"
        : isDark
        ? "rgba(56, 189, 248, 0.14)"
        : "rgba(37, 99, 235, 0.09)";
      const bgColor = bgStyle === "transparent" ? "none" : isDark ? "#0f172a" : "#ffffff";

      const minWY = latToWorldY(metrics.north, 20);
      const maxWY = latToWorldY(metrics.south, 20);
      const spanWY = Math.max(maxWY - minWY, 0.000001);

      const toSvgPixel = (pt) => {
        const x = offsetX + ((pt.lng - metrics.west) / (metrics.east - metrics.west)) * canvasW;
        const y = offsetY + ((latToWorldY(pt.lat, 20) - minWY) / spanWY) * canvasH;
        return { x, y };
      };

      const escXml = (str) =>
        String(str || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");

      let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalW} ${totalH}" width="${totalW}" height="${totalH}">\n`;

      if (bgColor !== "none") {
        svg += `  <rect width="${totalW}" height="${totalH}" fill="${bgColor}"/>\n`;
      }

      // Embedded map/satellite image if active
      const effectiveMap = mapCanvasForSvg || stitchedMapCanvas;
      if (isMapOrSatellite && effectiveMap) {
        try {
          const dataUrl = effectiveMap.toDataURL("image/png");
          svg += `  <image href="${dataUrl}" x="${offsetX}" y="${offsetY}" width="${canvasW}" height="${canvasH}" preserveAspectRatio="none"/>\n`;
        } catch (e) {}
      }

      // Metric Grid
      if (showGrid) {
        const maxDimM = Math.max(metrics.boxWidthMeters, metrics.boxHeightMeters);
        const metricStep = maxDimM <= 60 ? 5 : maxDimM <= 150 ? 10 : maxDimM <= 350 ? 20 : 50;
        const stepX = (metricStep / metrics.boxWidthMeters) * canvasW;
        const stepY = (metricStep / metrics.boxHeightMeters) * canvasH;

        svg += `  <g stroke="${isDark ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.08)"}" stroke-width="1">\n`;
        for (let x = offsetX; x <= offsetX + canvasW + 0.5; x += stepX) {
          svg += `    <line x1="${x.toFixed(1)}" y1="${offsetY}" x2="${x.toFixed(1)}" y2="${offsetY + canvasH}"/>\n`;
        }
        for (let y = offsetY; y <= offsetY + canvasH + 0.5; y += stepY) {
          svg += `    <line x1="${offsetX}" y1="${y.toFixed(1)}" x2="${offsetX + canvasW}" y2="${y.toFixed(1)}"/>\n`;
        }
        svg += `  </g>\n`;
      }

      // Polygons
      activePolygons.forEach((poly, pIdx) => {
        if (!poly || poly.length < 3) return;
        const pointsStr = poly
          .map((pt) => {
            const { x, y } = toSvgPixel(pt);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ");

        svg += `  <!-- Shape ${pIdx + 1} -->\n`;
        if (isMapOrSatellite) {
          svg += `  <polygon points="${pointsStr}" fill="none" stroke="${
            isSat ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.85)"
          }" stroke-width="8" stroke-linejoin="round"/>\n`;
        }
        svg += `  <polygon points="${pointsStr}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="4" stroke-linejoin="round"/>\n`;

        if (showVertices) {
          poly.forEach((pt, idx) => {
            const { x, y } = toSvgPixel(pt);
            svg += `  <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="5.5" fill="${strokeColor}" stroke="${
              isDark ? "#0f172a" : "#ffffff"
            }" stroke-width="2"/>\n`;
            if (poly.length <= 40) {
              svg += `  <text x="${(x + 9).toFixed(1)}" y="${(y - 7).toFixed(
                1
              )}" font-family="sans-serif" font-size="13" font-weight="bold" fill="${
                isDark ? "#ffffff" : "#0f172a"
              }">${idx + 1}</text>\n`;
            }
          });
        }
      });

      if (isGeoref) {
        svg += `  <rect x="0" y="0" width="${totalW}" height="${totalH}" fill="none" stroke="${strokeColor}" stroke-width="2" stroke-dasharray="8 8"/>\n`;
      }

      if (isBlueprint) {
        const textCol = isDark ? "#ffffff" : "#0f172a";
        const subCol = isDark ? "#cbd5e1" : "#475569";

        if (showDimensions) {
          const tl = toSvgPixel({ lat: metrics.vertexNorth, lng: metrics.vertexWest });
          const br = toSvgPixel({ lat: metrics.vertexSouth, lng: metrics.vertexEast });
          const dimY = offsetY - 22;
          svg += `  <line x1="${tl.x.toFixed(1)}" y1="${dimY}" x2="${br.x.toFixed(
            1
          )}" y2="${dimY}" stroke="${strokeColor}" stroke-width="2"/>\n`;
          svg += `  <text x="${((tl.x + br.x) / 2).toFixed(1)}" y="${
            dimY - 10
          }" text-anchor="middle" font-family="sans-serif" font-size="16" font-weight="bold" fill="${textCol}">Width: ${metrics.widthMeters.toFixed(
            1
          )} m</text>\n`;
        }

        if (showTitleBlock) {
          const tbW = Math.min(480, totalW * 0.48);
          const tbH = 106;
          const tbX = totalW - tbW - 16;
          const tbY = totalH - tbH - 16;
          svg += `  <rect x="${tbX}" y="${tbY}" width="${tbW}" height="${tbH}" fill="${
            isDark ? "#0f172a" : "#f8fafc"
          }" stroke="${isDark ? "#334155" : "#cbd5e1"}" stroke-width="1.5"/>\n`;
          svg += `  <text x="${tbX + 12}" y="${
            tbY + 24
          }" font-family="sans-serif" font-size="15" font-weight="bold" fill="${textCol}">${escXml(
            locationName || "Building Outline Template"
          )}</text>\n`;
          svg += `  <text x="${tbX + 12}" y="${
            tbY + 46
          }" font-family="sans-serif" font-size="11" fill="${subCol}">Area: ~${Math.round(
            metrics.areaM2
          ).toLocaleString()} m² | W: ${metrics.widthMeters.toFixed(1)}m × H: ${metrics.heightMeters.toFixed(
            1
          )}m</text>\n`;
          svg += `  <text x="${tbX + 12}" y="${
            tbY + 68
          }" font-family="monospace" font-size="9.5" fill="${subCol}">N ${metrics.north.toFixed(
            6
          )}, S ${metrics.south.toFixed(6)}, E ${metrics.east.toFixed(6)}, W ${metrics.west.toFixed(6)}</text>\n`;
          svg += `  <text x="${tbX + 12}" y="${
            tbY + 88
          }" font-family="sans-serif" font-size="10" fill="${subCol}">LoQation 360 • Georeferenced CAD Template</text>\n`;
        }
      }

      svg += `</svg>`;
      return svg;
    },
    [
      metrics,
      templateMode,
      bgStyle,
      isMapOrSatellite,
      stitchedMapCanvas,
      showGrid,
      showVertices,
      showDimensions,
      showTitleBlock,
      activePolygons,
      locationName,
    ]
  );

  // Helper to trigger browser file download reliably (with dataURL fallback)
  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  // Ensure stitched map canvas is ready if user clicks download immediately
  const ensureMapCanvasReady = async () => {
    if (!isMapOrSatellite) return null;
    if (stitchedMapCanvas) return stitchedMapCanvas;
    const lyrs = bgStyle === "satellite" ? "s" : bgStyle === "hybrid" ? "y" : "m";
    const canvas = await stitchMapTilesForBounds(metrics, lyrs);
    if (canvas) setStitchedMapCanvas(canvas);
    return canvas;
  };

  // Handle Download Action
  const handleDownload = async () => {
    if (!metrics) return;
    setIsDownloading(true);

    try {
      const safeName = (locationName || "Building_Outline").replace(/[^a-zA-Z0-9_-]/g, "_");
      const modeSuffix = templateMode === "georeference" ? "1-to-1_Calibration" : "Blueprint";
      const styleSuffix =
        bgStyle === "satellite"
          ? "Satellite"
          : bgStyle === "hybrid"
          ? "Hybrid"
          : bgStyle === "map"
          ? "Map"
          : bgStyle;
      const filename = `${safeName}_${modeSuffix}_${styleSuffix}.${exportFormat}`;

      const mapImg = await ensureMapCanvasReady();

      if (exportFormat === "svg") {
        const svgStr = generateSvgString(mapImg);
        const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
        triggerBlobDownload(blob, filename);
        setIsDownloading(false);
        return;
      }

      const exportCanvas = document.createElement("canvas");
      renderCanvas(exportCanvas, exportResolution, mapImg, false);

      exportCanvas.toBlob(
        (blob) => {
          if (blob) {
            triggerBlobDownload(blob, filename);
          } else {
            // Fallback via dataURL if canvas.toBlob returns null on large resolution
            const dataUrl = exportCanvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          setIsDownloading(false);
        },
        "image/png",
        1.0
      );
    } catch (err) {
      console.error("Failed to export template:", err);
      setIsDownloading(false);
    }
  };

  // Copy PNG directly to system clipboard
  const handleCopyToClipboard = async () => {
    if (!metrics || !navigator.clipboard || !window.ClipboardItem) return;
    setIsDownloading(true);
    try {
      const mapImg = await ensureMapCanvasReady();
      const exportCanvas = document.createElement("canvas");
      renderCanvas(exportCanvas, 2000, mapImg, false);

      exportCanvas.toBlob(async (blob) => {
        if (blob) {
          try {
            await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2500);
          } catch (e) {
            console.error("Clipboard write failed:", e);
          }
        }
        setIsDownloading(false);
      }, "image/png");
    } catch (e) {
      setIsDownloading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.65)", zIndex: 1060 }}
      tabIndex="-1"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden">
          {/* Header */}
          <div className="modal-header py-14 px-20 bg-neutral-50 border-bottom d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <div className="w-36-px h-36-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                <Icon icon="solar:download-square-bold" className="text-xl" />
              </div>
              <div>
                <h6 className="text-md fw-bold mb-0 text-dark">
                  Floorplan & CAD Template Generator
                </h6>
                <p className="text-xs text-secondary-light mb-0">
                  Export a georeferenced 1:1 building outline template with high-resolution satellite, roadmap, or CAD background.
                </p>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body p-20">
            {!metrics ? (
              <div className="alert alert-warning d-flex align-items-center gap-2 p-16 radius-8">
                <Icon icon="solar:info-circle-bold" className="text-xl flex-shrink-0" />
                <div>
                  No valid building outline coordinates found. Please draw at least one 3-vertex polygon in the Building Outline section first.
                </div>
              </div>
            ) : (
              <div className="row g-4">
                {/* Left: WYSIWYG Preview Display */}
                <div className="col-lg-7 d-flex flex-column">
                  {/* Preview Mode Bar */}
                  <div className="d-flex align-items-center justify-content-between mb-8">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-xs fw-bold text-dark d-inline-flex align-items-center gap-1">
                        <Icon icon="solar:eye-bold" className="text-primary-600" />
                        Live Preview
                      </span>
                      {isLoadingTiles && (
                        <span className="badge bg-primary-50 text-primary-600 text-xxs d-inline-flex align-items-center gap-1 px-8 py-2 radius-4">
                          <Icon icon="line-md:loading-twotone-loop" />
                          Loading high-res aerial tiles...
                        </span>
                      )}
                    </div>

                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 text-xxs fw-semibold d-inline-flex align-items-center gap-1 ${
                          previewTab === "canvas"
                            ? "btn-primary text-white"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setPreviewTab("canvas")}
                      >
                        <Icon icon="solar:gallery-Check-bold" />
                        Exact Export Preview (WYSIWYG)
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 text-xxs fw-semibold d-inline-flex align-items-center gap-1 ${
                          previewTab === "interactive"
                            ? "btn-primary text-white"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setPreviewTab("interactive")}
                      >
                        <Icon icon="solar:map-point-wave-bold" />
                        Interactive Map
                      </button>
                    </div>
                  </div>

                  <div
                    className="border radius-10 p-12 bg-neutral-900 d-flex flex-column align-items-center justify-content-center overflow-hidden position-relative shadow-sm flex-grow-1"
                    style={{ minHeight: "440px", height: "440px" }}
                  >
                    {/* Interactive Google Map View (Optional Tab) */}
                    <div
                      ref={mapContainerRef}
                      className="w-100 h-100 radius-8"
                      style={{
                        display: previewTab === "interactive" ? "block" : "none",
                      }}
                    />

                    {/* True WYSIWYG Canvas Preview (Used for ALL styles including Satellite & Map!) */}
                    <canvas
                      ref={canvasRef}
                      className="radius-6 shadow"
                      style={{
                        display: previewTab === "canvas" ? "block" : "none",
                        maxWidth: "98%",
                        maxHeight: "396px",
                        objectFit: "contain",
                      }}
                    />

                    {/* Bounding Box Information Ribbon */}
                    <div className="position-absolute bottom-0 start-0 m-12 bg-neutral-900 bg-opacity-85 text-white text-xxs px-10 py-4 radius-6 font-monospace d-flex align-items-center gap-2 shadow-sm">
                      <span className="fw-semibold">
                        Building: {metrics.widthMeters.toFixed(1)}m × {metrics.heightMeters.toFixed(1)}m
                      </span>
                      <span>•</span>
                      <span>Area: ~{Math.round(metrics.areaM2).toLocaleString()} m²</span>
                      <span>•</span>
                      <span>{metrics.allPointsCount} Vertices</span>
                      {bgStyle === "satellite" && (
                        <span className="badge bg-warning text-dark px-6 py-1 fw-bold">Satellite HD</span>
                      )}
                      {bgStyle === "hybrid" && (
                        <span className="badge bg-warning text-dark px-6 py-1 fw-bold">Hybrid HD</span>
                      )}
                      {bgStyle === "map" && (
                        <span className="badge bg-info text-white px-6 py-1 fw-semibold">Roadmap HD</span>
                      )}
                    </div>
                  </div>

                  {/* Template Info Alert */}
                  <div className="alert alert-info py-8 px-12 mt-12 mb-0 radius-8 text-xxs d-flex align-items-center gap-2">
                    <Icon icon="solar:verified-check-bold" className="text-info-600 text-lg flex-shrink-0" />
                    <div>
                      {templateMode === "georeference" ? (
                        <span>
                          <strong>1:1 Auto-Calibrated Fit:</strong> Die Vorlagen-Grenzen (inkl. 5% Randpuffer) entsprechen exakt den Standard-Koordinaten beim Grundriss-Upload (`N {metrics.north.toFixed(6)}, S {metrics.south.toFixed(6)}, E {metrics.east.toFixed(6)}, W {metrics.west.toFixed(6)}`). Wenn Sie Ihren Grundriss in diese Vorlage einzeichnen und unter <em>Levels & Floorplans</em> hochladen, sitzt er sofort <strong>100% passgenau</strong>!
                        </span>
                      ) : (
                        <span>
                          <strong>Architectural Blueprint:</strong> Enthält metrisches Maßstabs-Raster, Bemaßungslinien ({metrics.widthMeters.toFixed(1)}m × {metrics.heightMeters.toFixed(1)}m), Nordpfeil und Plankopf für CAD-Konstruktion und Dokumentation.
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Customization Controls */}
                <div className="col-lg-5 d-flex flex-column gap-12">
                  {/* Template Mode Selection */}
                  <div>
                    <label className="form-label text-xs fw-bold text-dark mb-6">
                      1. Template Layout Mode
                    </label>
                    <div className="row g-2">
                      <div className="col-6">
                        <button
                          type="button"
                          className={`w-100 p-10 radius-8 border text-start transition-2 ${
                            templateMode === "georeference"
                              ? "bg-primary-50 border-primary-600 text-primary-700 shadow-xs"
                              : "bg-white border-neutral-200 text-secondary-light hover-bg-neutral-50"
                          }`}
                          onClick={() => setTemplateMode("georeference")}
                        >
                          <div className="d-flex align-items-center gap-1 fw-semibold text-xs mb-1">
                            <Icon icon="solar:maximize-square-2-bold" />
                            1:1 Calibration Fit
                          </div>
                          <div className="text-xxs opacity-75">
                            Passgenau für direkten Floorplan-Upload ohne Verzerrung.
                          </div>
                        </button>
                      </div>
                      <div className="col-6">
                        <button
                          type="button"
                          className={`w-100 p-10 radius-8 border text-start transition-2 ${
                            templateMode === "blueprint"
                              ? "bg-primary-50 border-primary-600 text-primary-700 shadow-xs"
                              : "bg-white border-neutral-200 text-secondary-light hover-bg-neutral-50"
                          }`}
                          onClick={() => setTemplateMode("blueprint")}
                        >
                          <div className="d-flex align-items-center gap-1 fw-semibold text-xs mb-1">
                            <Icon icon="solar:ruler-pen-bold" />
                            Blueprint Layout
                          </div>
                          <div className="text-xxs opacity-75">
                            Mit Bemaßung, Nordpfeil & Metadaten-Plankopf.
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Multi-Shape Selection (if more than 1 polygon) */}
                  {normalizedAllPolys.length > 1 && (
                    <div>
                      <label className="form-label text-xs fw-bold text-dark mb-4">
                        Included Building Outlines
                      </label>
                      <select
                        className="form-select form-select-sm text-xs radius-6"
                        value={selectedShape}
                        onChange={(e) => setSelectedShape(e.target.value)}
                      >
                        <option value="all">
                          All Shapes Combined ({normalizedAllPolys.length} polygons)
                        </option>
                        {normalizedAllPolys.map((poly, idx) => (
                          <option key={idx} value={String(idx)}>
                            Shape {idx + 1} only ({poly.length} vertices)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Visual Style & Imagery Options */}
                  <div className="p-12 radius-8 bg-neutral-50 border">
                    <label className="form-label text-xs fw-bold text-dark mb-8">
                      2. Background & Visual Style
                    </label>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-10">
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 d-inline-flex align-items-center gap-1 ${
                          bgStyle === "satellite"
                            ? "btn-warning text-dark fw-bold shadow-xs"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setBgStyle("satellite")}
                        title="Pure aerial satellite photography without label clutter"
                      >
                        <Icon icon="solar:earth-bold" className="text-base" />
                        Satellite
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 d-inline-flex align-items-center gap-1 ${
                          bgStyle === "hybrid"
                            ? "btn-warning text-dark fw-bold shadow-xs"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setBgStyle("hybrid")}
                        title="Satellite photography with street and place labels"
                      >
                        <Icon icon="solar:global-bold" className="text-base" />
                        Hybrid
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 d-inline-flex align-items-center gap-1 ${
                          bgStyle === "map"
                            ? "btn-primary text-white fw-semibold shadow-xs"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setBgStyle("map")}
                        title="Standard street roadmap view"
                      >
                        <Icon icon="solar:map-bold" className="text-base" />
                        Roadmap
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 d-inline-flex align-items-center gap-1 ${
                          bgStyle === "white"
                            ? "btn-primary text-white fw-semibold"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setBgStyle("white")}
                      >
                        <Icon icon="solar:document-linear" />
                        White
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 d-inline-flex align-items-center gap-1 ${
                          bgStyle === "transparent"
                            ? "btn-primary text-white fw-semibold"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setBgStyle("transparent")}
                      >
                        <Icon icon="solar:eraser-linear" />
                        Transparent
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs radius-6 px-10 py-1 d-inline-flex align-items-center gap-1 ${
                          bgStyle === "dark"
                            ? "btn-primary text-white fw-semibold"
                            : "btn-outline-neutral bg-white text-secondary-light"
                        }`}
                        onClick={() => setBgStyle("dark")}
                      >
                        <Icon icon="solar:moon-linear" />
                        Blueprint Navy
                      </button>
                    </div>

                    {/* Layer Toggles */}
                    <div className="d-flex flex-column gap-2 text-xs pt-6 border-top">
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="toggleGrid"
                          checked={showGrid}
                          onChange={(e) => setShowGrid(e.target.checked)}
                        />
                        <label className="form-check-label text-xxs text-dark cursor-pointer" htmlFor="toggleGrid">
                          Show Architectural Metric Grid (5m / 10m scale)
                        </label>
                      </div>

                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="toggleVertices"
                          checked={showVertices}
                          onChange={(e) => setShowVertices(e.target.checked)}
                        />
                        <label className="form-check-label text-xxs text-dark cursor-pointer" htmlFor="toggleVertices">
                          Show Corner Vertices & Point Numbers (1..{metrics.allPointsCount})
                        </label>
                      </div>

                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="toggleEdgeLengths"
                          checked={showEdgeLengths}
                          onChange={(e) => setShowEdgeLengths(e.target.checked)}
                        />
                        <label className="form-check-label text-xxs text-dark cursor-pointer" htmlFor="toggleEdgeLengths">
                          Show Wall / Segment Lengths in Meters (m)
                        </label>
                      </div>

                      {templateMode === "blueprint" && (
                        <>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="toggleDimensions"
                              checked={showDimensions}
                              onChange={(e) => setShowDimensions(e.target.checked)}
                            />
                            <label className="form-check-label text-xxs text-dark cursor-pointer" htmlFor="toggleDimensions">
                              Show Overall Building Dimension Lines (W × L)
                            </label>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="toggleNorth"
                              checked={showNorthArrow}
                              onChange={(e) => setShowNorthArrow(e.target.checked)}
                            />
                            <label className="form-check-label text-xxs text-dark cursor-pointer" htmlFor="toggleNorth">
                              Show Compass / North Arrow
                            </label>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="toggleTitle"
                              checked={showTitleBlock}
                              onChange={(e) => setShowTitleBlock(e.target.checked)}
                            />
                            <label className="form-check-label text-xxs text-dark cursor-pointer" htmlFor="toggleTitle">
                              Show Project Metadata Title Block
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* File Format & Resolution Options */}
                  <div className="row g-2">
                    <div className={exportFormat === "png" ? "col-7" : "col-12"}>
                      <label className="form-label text-xs fw-bold text-dark mb-4">
                        3. File Format
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className={`btn btn-sm radius-6 flex-grow-1 text-xs d-flex align-items-center justify-content-center gap-1 ${
                            exportFormat === "png"
                              ? "btn-primary"
                              : "btn-outline-neutral bg-white text-secondary-light"
                          }`}
                          onClick={() => setExportFormat("png")}
                        >
                          <Icon icon="solar:gallery-linear" />
                          PNG Image
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm radius-6 flex-grow-1 text-xs d-flex align-items-center justify-content-center gap-1 ${
                            exportFormat === "svg"
                              ? "btn-primary"
                              : "btn-outline-neutral bg-white text-secondary-light"
                          }`}
                          onClick={() => setExportFormat("svg")}
                        >
                          <Icon icon="solar:code-file-linear" />
                          SVG Vector
                        </button>
                      </div>
                    </div>

                    {exportFormat === "png" && (
                      <div className="col-5">
                        <label className="form-label text-xs fw-bold text-dark mb-4">
                          Resolution
                        </label>
                        <select
                          className="form-select form-select-sm radius-6 text-xs"
                          value={exportResolution}
                          onChange={(e) => setExportResolution(Number(e.target.value))}
                          style={{ height: "33px" }}
                        >
                          <option value={2000}>2000 px (Standard)</option>
                          <option value={3000}>3000 px (High-Res)</option>
                          <option value={4096}>4096 px (Ultra 4K)</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer py-12 px-20 bg-neutral-50 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="text-xs text-secondary-light font-monospace">
              {metrics ? (
                <span>
                  Calibrated Bounds: N {metrics.north.toFixed(6)} | S {metrics.south.toFixed(6)} | E {metrics.east.toFixed(6)} | W {metrics.west.toFixed(6)}
                </span>
              ) : (
                "Draw outline first."
              )}
            </span>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary radius-8 px-14 text-xs d-inline-flex align-items-center gap-1"
                onClick={handleCopyToClipboard}
                disabled={!metrics || isDownloading || isLoadingTiles}
                title="Copy template image directly to clipboard"
              >
                <Icon
                  icon={copySuccess ? "solar:check-circle-bold" : "solar:copy-linear"}
                  className={copySuccess ? "text-success-600 text-base" : "text-base"}
                />
                <span>{copySuccess ? "Copied!" : "Copy Image"}</span>
              </button>
              <button type="button" className="btn btn-secondary radius-8 px-16 text-xs" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary radius-8 px-20 text-xs fw-semibold d-flex align-items-center gap-2 shadow-xs"
                onClick={handleDownload}
                disabled={!metrics || isDownloading || isLoadingTiles}
              >
                {isDownloading ? (
                  <>
                    <Icon icon="line-md:loading-twotone-loop" className="text-base" />
                    <span>Preparing Download...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="solar:download-square-bold" className="text-base" />
                    <span>Download {exportFormat.toUpperCase()} Template</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
